import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { TravelTwoIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getJourneys } from '../../../api/products/get-journeys'
import { getOtoIndividualProducts } from '../../../api/products/get-oto-individual-products'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import {
  PromoCodeApplicationEnum,
  promoCodeApplicationMapping
} from '../../../lib/types/enums/promo-code/promo-code-application.enum'
import {
  PromoCodeTypeEnum,
  promoCodeTypeMapping
} from '../../../lib/types/enums/promo-code/promo-code-type.enum'
import { JourneyFilters } from '../../../lib/types/models/product/journey'
import { PromoCodeInput } from '../../../lib/types/models/promo-code/promo-code-input'
import { BaseFormProps, IdOnly, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformJourneyFiltersToUrl,
  transformUrlToJourneyFilters
} from '../../../lib/utils/filters-mapping/journey'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import ControlledSwitch from '../../elements/controlled-switch'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'
import JourneyListSelection from '../enrollments/enrolment-create-proposition-steps/journey-list-selection'
import JourneyListSelectionFilter from '../enrollments/enrolment-create-proposition-steps/journey-list-selection-filter'
import ProductJourneyPlaceListSelection from '../enrollments/enrolment-create-proposition-steps/product-journey-place-list-selection'
import {
  getOtoIndividualProducts as getOtoIndividualProductsFromRows,
  getRegionOfStayLabel
} from '../enrollments/utils/oto-individual-utils'

const formSchema: ZodType<NullableTransformer<PromoCodeInput>> = z
  .object({
    name: createStringSchema('NOM'),
    code: createStringSchema('code'),
    discount: createNumberSchema('remise', { isPositive: true }),
    validFrom: createDateStringSchema({ inputName: 'valable à partir du' }),
    validUntil: createDateStringSchema({ inputName: "valable jusqu'à" }),
    type: createEnumSchema({ enumType: PromoCodeTypeEnum, fieldName: 'type' }),
    includedInsurance: z.boolean().nullish(),
    journeys: createNumberArraySchema('options', { isInteger: true }).nullish(),
    productJourneyPlaces: createNumberArraySchema('lieux de séjour', {
      isInteger: true
    }).nullish(),
    application: createEnumSchema({
      enumType: PromoCodeApplicationEnum,
      fieldName: 'application'
    }).nullish()
  })
  .superRefine((data, ctx) => {
    //check if type is PromoCodeTypeEnum.PERCENTAGE discount should be between 0 and 100
    if (data.type === PromoCodeTypeEnum.PERCENTAGE && (data.discount < 0 || data.discount > 100)) {
      ctx.addIssue({
        path: ['discount'],
        message: 'La remise doit être comprise entre 0 et 100',
        code: 'custom'
      })
    }
  })

const PromoCodeForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<PromoCodeInput, true>) => {
  const form = useForm<PromoCodeInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { validFrom, validUntil } = form.watch()

  const { data: journeys } = useQuery({
    queryKey: ['journeys'],
    queryFn: () => getJourneys<'BASIC'>()
  })

  const { data: otoIndividualProducts } = useQuery({
    queryKey: ['oto-individual-products'],
    queryFn: () => getOtoIndividualProducts()
  })

  // every journey place of the One to One Individuel products, for the select options
  const journeyPlaces = getOtoIndividualProductsFromRows(otoIndividualProducts ?? []).flatMap(
    (product) => product.journeyPlaces
  )

  // what the selection modals hold until they are confirmed
  const [pendingJourneys, setPendingJourneys] = useState<IdOnly[]>([])
  const [pendingJourneyPlaces, setPendingJourneyPlaces] = useState<IdOnly[]>([])

  const { modalState, toggleModal, closeModel } = useModalManager<
    'journey-selection' | 'journey-place-selection'
  >(['journey-selection', 'journey-place-selection'])

  const journeyFilter = useFilters<JourneyFilters>({
    initialFilters: { published: true },
    options: { allowSearchParam: false },
    transformFilterToUrl: transformJourneyFiltersToUrl,
    transformUrlToFilter: transformUrlToJourneyFilters
  })

  useEffect(() => {
    if (validFrom && validUntil && validFrom > validUntil) {
      // The start date cannot be after the end date
      form.setError('validFrom', { message: 'La date de début ne peut être après la date de fin' })
    } else form.clearErrors('validFrom')
  }, [validFrom, validUntil])

  return (
    <Form form={form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className="flex flex-col gap-4"
      >
        <Box className="flex flex-col gap-4">
          <Typography variant="body-medium" color={tailwindColors.primary.DEFAULT}>
            Détails du code promo
          </Typography>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label="NOM" type="text" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label="Code" type="text" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="discount"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label="Remise" type="number" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput<PromoCodeTypeEnum>
                    {...field}
                    required
                    options={Object.values(PromoCodeTypeEnum).map((priority) => ({
                      label: promoCodeTypeMapping[priority].label,
                      value: priority
                    }))}
                    label={'Type'}
                    selectedOption={getSelectedEnumOption(field.value, promoCodeTypeMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="application"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput<PromoCodeApplicationEnum>
                    {...field}
                    options={Object.values(PromoCodeApplicationEnum).map((priority) => ({
                      label: promoCodeApplicationMapping[priority],
                      value: priority
                    }))}
                    label={"Mode d'application"}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      promoCodeApplicationMapping
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'validFrom'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    label="Valable à partir du"
                    disabledPastDates
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'validUntil'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    label="Valable jusqu'à"
                    disabledPastDates
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'journeys'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <div className={'flex flex-row items-end gap-2'}>
                    <div className={'flex-1'}>
                      <ControlledMultiSelectInput
                        {...field}
                        label={'Séjours concernés'}
                        variant={'primary'}
                        searchable
                        options={
                          journeys?.data.map((journey) => ({
                            label: journey.reference,
                            value: journey.id
                          })) ?? []
                        }
                        selectedOptions={
                          field.value?.map((journeyId) => ({
                            label:
                              journeys?.data.find((journey) => journey.id === journeyId)
                                ?.reference ?? '',
                            value: journeyId
                          })) ?? []
                        }
                      />
                    </div>

                    <Button
                      type={'button'}
                      variant={'secondary'}
                      label={'Sélectionner des séjours'}
                      onClick={() => {
                        setPendingJourneys((field.value ?? []).map((id) => ({ id })))
                        toggleModal('journey-selection')
                      }}
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'productJourneyPlaces'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <div className={'flex flex-row items-end gap-2'}>
                    <div className={'flex-1'}>
                      <ControlledMultiSelectInput
                        {...field}
                        label={'Lieux de séjour concernés (OTO Individuel)'}
                        variant={'primary'}
                        searchable
                        options={journeyPlaces.map((journeyPlace) => ({
                          label: getRegionOfStayLabel(journeyPlace),
                          value: journeyPlace.id
                        }))}
                        selectedOptions={
                          field.value?.map((journeyPlaceId) => {
                            const journeyPlace = journeyPlaces.find(
                              (journeyPlace) => journeyPlace.id === journeyPlaceId
                            )
                            return {
                              label: journeyPlace ? getRegionOfStayLabel(journeyPlace) : '',
                              value: journeyPlaceId
                            }
                          }) ?? []
                        }
                      />
                    </div>

                    <Button
                      type={'button'}
                      variant={'secondary'}
                      label={'Sélectionner des lieux de séjour'}
                      onClick={() => {
                        setPendingJourneyPlaces((field.value ?? []).map((id) => ({ id })))
                        toggleModal('journey-place-selection')
                      }}
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'includedInsurance'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSwitch {...field} label={"Inclure l'assurance"} />
                </FormControl>
              </FormItem>
            )}
          />

          <ValidationButtons {...validationButtonProps} />
        </Box>
      </form>

      <Modal
        hideActions
        size={'large'}
        titleAlignment={'left'}
        icon={TravelTwoIcon}
        title={'Sélectionner les séjours concernés'}
        isOpen={modalState['journey-selection']}
        onCancel={() => closeModel('journey-selection')}
      >
        <div className={`space-y-4 ${MODAL_BODY_CLASSNAME}`}>
          <JourneyListSelectionFilter
            filter={journeyFilter.filter}
            onResetFilter={journeyFilter.resetFilters}
            onSelectedFilterChange={journeyFilter.onSelectedFilterChange}
          />
          <JourneyListSelection
            mode={'multiple'}
            tableTitle={'Sélectionner un ou plusieurs séjour(s)'}
            journeys={pendingJourneys}
            filterProps={journeyFilter}
            onSelectJourneys={setPendingJourneys}
            onSubmit={() => {
              form.setValue(
                'journeys',
                pendingJourneys.map((journey) => journey.id),
                { shouldDirty: true, shouldValidate: true }
              )
              closeModel('journey-selection')
            }}
          />
        </div>
      </Modal>

      <Modal
        hideActions
        size={'large'}
        titleAlignment={'left'}
        icon={TravelTwoIcon}
        title={'Sélectionner les lieux de séjour concernés'}
        isOpen={modalState['journey-place-selection']}
        onCancel={() => closeModel('journey-place-selection')}
      >
        <div className={`space-y-4 ${MODAL_BODY_CLASSNAME}`}>
          <ProductJourneyPlaceListSelection
            journeyPlaces={pendingJourneyPlaces}
            onSelectJourneyPlaces={setPendingJourneyPlaces}
            onSubmit={() => {
              form.setValue(
                'productJourneyPlaces',
                pendingJourneyPlaces.map((journeyPlace) => journeyPlace.id),
                { shouldDirty: true, shouldValidate: true }
              )
              closeModel('journey-place-selection')
            }}
          />
        </div>
      </Modal>
    </Form>
  )
}

export default PromoCodeForm
